// COPIED from ptc-core web/src/feedback/feedbackContext.ts @ de39fff (fleet feedback contract v1, 2026-10-21).
// Changes from the source: `buildSha` is statetax's own build stamp (readBuildInfo, the SHA /build-info.json
// reports) instead of core's BUILD_ID. Contract v1.1 (2026-10-27): `uiLanguage`, the UI's resolved language (i18next),
// never the browser's; the server answers its refusals in it (C10). Conformance: src/components/feedback/FeedbackWidgetConformance.test.tsx.
/**
 * The context a feedback report carries automatically (feedback-loop spec §3.2) — shown to the
 * reporter before it is sent, because telemetry a person cannot see erodes trust in exactly the
 * people this feature is for.
 *
 * ⚠ THIS IS A DATA EGRESS POINT, like `reportClientError.ts`. Two rules differ from that module
 * on purpose, and the server enforces both again (`FeedbackContentGuard`):
 *   - the ROUTE keeps its query string, because filter and view state live there (filter contract
 *     v2 §11) and the developer must open exactly what the reporter saw — BUT free-text search
 *     parameters are stripped: `?q=John+Smith` is a client's name, not view state;
 *   - recent errors come from the SAME ring `reportClientError` already keeps (BL-042: no second
 *     capture path), so they carry only that module's PII-free shape.
 */
import { recentClientErrors } from '../telemetry/reportClientError';
import { readBuildInfo } from '../lib/buildInfo';
import i18n from '../i18n';

/** Query parameters that carry typed text rather than view state. */
export const FREE_TEXT_PARAMS = ['q', 'search', 'query', 'term'] as const;

export interface FeedbackRecentError {
  name: string;
  message: string;
  pathname: string;
  timestamp: string;
}

export interface FeedbackContext {
  route: string;
  buildSha: string;
  viewport: string;
  userAgent: string;
  clientTime: string;
  tz: string;
  recentErrors: FeedbackRecentError[];
  /** v1.1: the language the staff member's UI shows (`en`, `es`), from the toggle, not the browser. */
  uiLanguage: string;
}

/** `pathname + search`, minus every free-text parameter. */
export function routeWithoutFreeText(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  for (const key of [...params.keys()]) {
    if ((FREE_TEXT_PARAMS as readonly string[]).includes(key.toLowerCase())) params.delete(key);
  }
  const rest = params.toString();
  return rest ? `${pathname}?${rest}` : pathname;
}

export function buildFeedbackContext(now: Date = new Date()): FeedbackContext {
  return {
    route: routeWithoutFreeText(window.location.pathname, window.location.search),
    // buildInfo.ts: null is the honest answer outside a deploy build, rendered as "unknown", never substituted.
    buildSha: readBuildInfo().buildSha ?? 'unknown',
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    userAgent: navigator.userAgent,
    clientTime: now.toISOString(),
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'unknown',
    recentErrors: recentClientErrors().map(({ name, message, pathname, timestamp }) => ({
      name,
      message,
      pathname,
      timestamp,
    })),
    uiLanguage: i18n.resolvedLanguage ?? i18n.language ?? 'en',
  };
}
